import Vue from 'vue';
import toastr from 'toastr';
import XLSX from 'xlsx';

import rootStore from '@/store';
import { i18n } from '@/plugins/i18n';
import { doAsync, setAsyncValue } from '@/utils/async';
import { calert, handleUserEditionError } from '@/utils/main';
import * as actionsTypes from './action-types';
import * as mutationsTypes from './mutation-types';
import { GET_TEAMS } from '@/store/mutation-types';
import ErrorCode from '@/utils/classes/ErrorCode';


/**
 * Handles the XHR response of a team edition/delection/creation
 * @param  {Object} response - XHR response
 * @param  {String} message  - translatable message
 * @return {[type]}          [description]
 */
function handleTeamsResponse(response, message) {
  if (!response || !response.data) throw response;
  if (response.data instanceof Array) {
    toastr.success(i18n.t(message));
    const data = (response.data instanceof Array ? response.data : [])
      .map(team => ({
        ...team,
        users: team.users.map(user => ({ ...user, label: i18n.t('fullNameFormat', user) })),
      }));
    setAsyncValue(rootStore, GET_TEAMS, data);
    return data;
  }
  toastr.error(i18n.t('message.CONNECTION_ERROR'));
  throw response.data;
}

function handleUsersResponse(store, response, message) {
  if (!response) throw response;
  if (response.data instanceof Array) {
    toastr.success(i18n.t(message));
    const users = response.data.map(user => ({ ...user, label: i18n.t('fullNameFormat', user) }));
    setAsyncValue(store, mutationsTypes.GET_CONFIG_USERS, users);
    return users;
  }
  if (response.data instanceof Object) {
    handleUserEditionError(response);
    throw response.data;
  } else {
    toastr.error(i18n.t('message.CONNECTION_ERROR'));
    throw response.data;
  }
}


export default {
  /**
   * Fetch indicator schemes
   * @param {Object} store - Vuex store
   */
  async [actionsTypes.FETCH_SCHEMES](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/config/schemes/get.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_SCHEMES,
      callback: response => (response.data instanceof Array ? response.data : []),
    });
  },

  /** Create a scheme of indicators */
  async [actionsTypes.CREATE_SCHEME](store) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/config/schemes/create.php',
      },
      mutationTypes: mutationsTypes.GET_SCHEMES,
      callback: response => (response.data instanceof Array ? response.data : []),
    });
  },

  /** Edit a scheme of indicators */
  async [actionsTypes.EDIT_SCHEME](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/config/schemes/edit.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_SCHEMES,
      callback: response => (response.data instanceof Array ? response.data : []),
    });
  },

  /** Delete a scheme of indicators */
  async [actionsTypes.DELETE_SCHEME](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/config/schemes/delete.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_SCHEMES,
      callback: response => (response.data instanceof Array ? response.data : []),
    })
      .then((response) => {
        if (response.data instanceof Array) {
          toastr.success(i18n.t('message.CONFIG_SCHEME_DELETED'));
        }
        return response;
      });
  },

  /** Change a company parameter */
  async [actionsTypes.SET_COMPANY_PARAM](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/config/setParams.php',
      data: payload,
    })
      .then((response) => {
        if (response && typeof response.data === 'object') {
          store.commit(mutationsTypes.SET_COMPANY, response.data);
          toastr.success(i18n.t('message.SAVED'));
          return response.data;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw response;
      });
  },

  /** Add/Edit a team */
  async [actionsTypes.EDIT_TEAM](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/config/updateTeam.php',
      data: { ...payload, config: 1 },
    })
      .then(response => handleTeamsResponse(response, 'message.TEAM_UPDATED'));
  },

  /** Add/Edit a user */
  async [actionsTypes.EDIT_USER](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/config/updateUser.php',
      data: { ...payload, config: 1, locale: i18n.t('min') },
    })
      .then(response => handleUsersResponse(store, response, 'message.USER_UPDATED'))
      .catch((error) => {
        if (error.response && error.response.data) {
          const data = error.response.data;
          if (data.code === ErrorCode.CONFIG_CANT_REMOVE_LAST_ADMIN) {
            calert(i18n.t('message.CONFIG_CANT_REMOVE_LAST_ADMIN'));
            const users = data.users.map(user => ({ ...user, label: i18n.t('fullNameFormat', user) }));
            setAsyncValue(rootStore, mutationsTypes.GET_CONFIG_USERS, users);
            return;
          }
        }
        throw error;
      });
  },

  /** Add multiple users */
  async [actionsTypes.ADD_USERS](store, payload) {
    store.commit(mutationsTypes.SET_SEND_USER_EMAIL_LOADING, true);
    const reader = new FileReader();
    reader.onload = (e) => {
      // Get first sheet
      const wb = XLSX.read(e.target.result, { type: 'binary' });
      if (!wb.SheetNames || !wb.SheetNames.length) {
        toastr.error(i18n.t('message.XLSX_FILE_ERROR'));
        throw 0;
      }

      // Parse
      const users = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1 });
      if (!users || !users.length) {
        toastr.error(i18n.t('message.XLSX_FILE_SEEMS_EMPTY'));
        throw 0;
      }

      // Check that headers are in the right place
      const headers = users[0];
      if (
        headers[0] !== i18n.t('message.FIRST_NAME')
        || headers[1] !== i18n.t('message.LAST_NAME')
        || headers[2] !== i18n.t('message.TEAM')
        || headers[3] !== i18n.t('message.EMAIL')
      ) {
        toastr.error(i18n.t('message.XLSX_KEEP_TEMP_FORMAT'));
        throw 0;
      }

      users.splice(0, 1);
      toastr.info(i18n.t('message.ADDING_USERS'), { timeOut: 30000 });

      return Vue.axios({
        method: 'post',
        url: '/server/config/addUsers.php',
        data: { users, config: 1, locale: i18n.t('min') },
      })
        .then((response) => {
          toastr.clear();
          store.commit(mutationsTypes.SET_SEND_USER_EMAIL_LOADING, false);
          return handleUsersResponse(store, response, 'message.USERS_ADDED');
        });
    };
    reader.readAsBinaryString(payload.file);
  },

  /** Delete a team */
  async [actionsTypes.DELETE_TEAMS](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/config/deleteTeams.php',
      data: { ...payload, config: 1 },
    })
      .then(response => handleTeamsResponse(response, 'message.TEAMS_REMOVED'))
      .catch((error) => {
        if (error.response && error.response.data) {
          const data = error.response.data;
          if (data.code === ErrorCode.CONFIG_CANT_DELETE_ADMIN_TEAM) {
            calert(i18n.t('message.CONFIG_CANT_DELETE_ADMIN_TEAM'));
            return;
          }
        }
        throw error;
      });
  },

  /** Delete a user */
  async [actionsTypes.DELETE_USERS](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/config/deleteUsers.php',
      data: { ...payload, config: 1 },
    })
      .then(response => handleUsersResponse(store, response, 'message.USERS_REMOVED'))
      .catch((error) => {
        if (error.response && error.response.data) {
          const data = error.response.data;
          if (data.code === ErrorCode.CONFIG_CANT_DELETE_SELF) {
            calert(i18n.t('message.CONFIG_CANT_DELETE_SELF'));
            return;
          }
        }
        throw error;
      });
  },

  /** Reset users passwords and send them their new credentials */
  async [actionsTypes.SEND_USERS_CREDENTIALS](store, payload) {
    store.commit(mutationsTypes.SET_SEND_USER_EMAIL_LOADING, true);
    return Vue.axios({
      method: 'post',
      url: '/server/config/sendUsersCredentials.php',
      data: { ...payload, config: 1, locale: i18n.t('min') },
    })
      .then((response) => {
        store.commit(mutationsTypes.SET_SEND_USER_EMAIL_LOADING, false);
        if (response && response.data === 1) {
          toastr.success(i18n.t('message.USER_UPDATED'));
        } else {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },

  /** Lock user accounts */
  async [actionsTypes.LOCK_USERS](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/config/lockUsers.php',
      data: { ...payload, config: 1 },
    })
      .then(response => handleUsersResponse(
        store,
        response,
        payload.lock ? 'message.CONFIG_ACCOUNTS_DISABLED' : 'message.CONFIG_ACCOUNTS_ENABLED',
      ));
  },

  /** Lock team accounts */
  async [actionsTypes.LOCK_TEAMS](store, payload) {
    return Vue.axios({
      method: 'post',
      url: '/server/config/lockTeams.php',
      data: { ...payload, config: 1 },
    })
      .then(response => handleTeamsResponse(
        response,
        payload.lock ? 'message.CONFIG_ACCOUNTS_DISABLED' : 'message.CONFIG_ACCOUNTS_ENABLED',
      ));
  },

  /** Fetch the start-up templates of this company */
  async [actionsTypes.FETCH_STARTUP_TEMPLATES](store) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/config/startup-templates/get.php',
      },
      mutationTypes: mutationsTypes.GET_STARTUP_TEMPLATES,
      callback: (response) => {
        if (response && response.data instanceof Array) {
          return response.data;
        }
        throw response;
      },
    });
  },

  /** Create/Update a start-up template */
  async [actionsTypes.SET_STARTUP_TEMPLATE](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/config/startup-templates/set.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_STARTUP_TEMPLATES,
      callback: (response) => {
        if (response && response.data instanceof Array) {
          toastr.success(i18n.t('message.SETTING_SAVED'));
          return response.data;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw response;
      },
    });
  },

  /** Delete a start-up template */
  async [actionsTypes.DELETE_STARTUP_TEMPLATE](store, payload) {
    return doAsync(store, {
      config: {
        method: 'post',
        url: '/server/config/startup-templates/delete.php',
        data: payload,
      },
      mutationTypes: mutationsTypes.GET_STARTUP_TEMPLATES,
      callback: (response) => {
        if (response && response.data instanceof Array) {
          toastr.success(i18n.t('message.GW_ITEM_DELETED'));
          return response.data;
        }
        toastr.error(i18n.t('message.CONNECTION_ERROR'));
        throw response;
      },
    });
  },

  /** Get current team's users for configuration */
  async [actionsTypes.FETCH_CONFIG_USERS](store, payload) {
    return doAsync(store, {
      config: {
        method: 'get',
        url: '/server/admin/users',
        params: payload,
      },
      mutationTypes: mutationsTypes.GET_CONFIG_USERS,
      callback: response => (response.data instanceof Array ? response.data : []).map(user => ({
        ...user,
        label: `${user.firstName} ${user.lastName}`,
      })),
    })
      .then(({data}) => {
        if (!(data instanceof Array)) {
          toastr.error(i18n.t('message.CONNECTION_ERROR'));
        }
      });
  },
};
